import { firstValueFrom } from 'rxjs';
import { Injectable, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { AuthApiService } from './api/auth-api.service';
import { LoginRequest, AuthResponse, User } from '../models/auth.model';

const ACCESS_TOKEN_KEY = 'liessin_web_app_access_token';
const REFRESH_TOKEN_KEY = 'liessin_web_app_refresh_token';
const USER_KEY = 'liessin_web_app_user';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly currentUser = signal<User | null>(this.loadUser());
  private readonly accessToken = signal<string | null>(this.loadAccessToken());

  readonly user = this.currentUser.asReadonly();
  readonly isLoggedIn = computed(() => !!this.accessToken());

  constructor(
    private authApi: AuthApiService,
    private router: Router
  ) {}

  login(credentials: LoginRequest): Promise<void> {
    return new Promise((resolve, reject) => {
      this.authApi.authenticate(credentials).subscribe({
        next: (res) => {
          this.handleAuthResponse(res, () => {
            if (!this.currentUser()) {
              this.loadCurrentUser().then(() => resolve());
            } else {
              resolve();
            }
          });
        },
        error: (err) => reject(err),
      });
    });
  }

  async loginWithGoogle(credential: string): Promise<void> {
    const response = await firstValueFrom(this.authApi.googleAuthenticate(credential));
    this.handleAuthResponse(response, () => {});
  }

  hasPermission(permission: string): boolean { return this.user()?.permissions?.includes(permission) ?? false; }
  isStaff(): boolean { return (this.user()?.roles ?? []).some(role => ['Admin', 'Analista'].includes(role)); }
  isApplicant(): boolean {
    const roles = this.user()?.roles ?? [];
    return roles.includes('Requerente') && !roles.some(role => ['Admin', 'Analista'].includes(role));
  }

  logout(): void {
    this.clearStorage();
    this.currentUser.set(null);
    this.accessToken.set(null);
    this.router.navigate(['/login']);
  }

  getAccessToken(): string | null {
    return this.accessToken();
  }

  /** Chamado pelo interceptor quando recebe 401; tenta refresh e retorna novo token ou null */
  refreshSession(): Promise<string | null> {
    const refresh = this.getRefreshToken();
    if (!refresh) return Promise.resolve(null);
    return new Promise((resolve) => {
      this.authApi.refreshAuthenticate(refresh).subscribe({
        next: (res) => {
          this.handleAuthResponse(res, () => resolve(this.getAccessToken()));
        },
        error: () => {
          this.logout();
          resolve(null);
        },
      });
    });
  }

  /** Carrega usuário atual do backend (após login ou refresh). */
  loadCurrentUser(): Promise<User | null> {
    return new Promise((resolve) => {
      this.authApi.getMe().subscribe({
        next: (u) => {
          this.currentUser.set(u);
          this.persistUser(u);
          resolve(u);
        },
        error: () => {
          this.currentUser.set(null);
          resolve(null);
        },
      });
    });
  }

  private handleAuthResponse(res: AuthResponse, onDone: () => void): void {
    const token = res.accessToken ?? res.token ?? res.access_token ?? null;
    const refresh = res.refreshToken ?? res.refresh_token ?? null;

    // Tenta extrair o usuário aninhado ou constrói a partir dos campos raiz
    const user: User | null =
      res.user ??
      (res.id != null && res.email
        ? { id: res.id, email: res.email, name: res.name, roles: res.roles, permissions: res.permissions }
        : null);

    if (token) {
      this.accessToken.set(token);
      localStorage.setItem(ACCESS_TOKEN_KEY, token);
    }
    if (refresh) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
    }
    if (user) {
      this.currentUser.set(user);
      this.persistUser(user);
    }
    onDone();
  }

  private loadAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  private loadUser(): User | null {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? (JSON.parse(raw) as User) : null;
    } catch {
      return null;
    }
  }

  private persistUser(u: User | null): void {
    if (u) {
      localStorage.setItem(USER_KEY, JSON.stringify(u));
    } else {
      localStorage.removeItem(USER_KEY);
    localStorage.removeItem('liessin_last_institution_id');
    }
  }

  private clearStorage(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem('liessin_last_institution_id');
  }
}
